import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import LoginPage from "@/pages/LoginPage";
import SubmissionsPage from "@/pages/SubmissionsPage";
import AssessmentEditorPage from "@/pages/AssessmentEditorPage";
import UsersAdminPage from "@/pages/UsersAdminPage";
import PrintView from "@/pages/PrintView";
import ClinicalSnapshotPage from "@/pages/ClinicalSnapshotPage";
import ClinicalReferencePage from "@/pages/ClinicalReferencePage";
import DoctorPanelPage from "@/pages/DoctorPanelPage";
import SymptomTrendsPage from "@/pages/SymptomTrendsPage";

function Loading() {
  return <div className="flex min-h-screen items-center justify-center text-sm text-stone-400">Loading…</div>;
}

function Protected({ children, roles }) {
  const { user, ready } = useAuth();
  if (!ready) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function Landing() {
  const { user, ready } = useAuth();
  if (!ready) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <Navigate to="/users" replace />;
  // a doctor opens on their caseload, not on a flat list of submissions
  if (user.role === "doctor") return <Navigate to="/panel" replace />;
  return <Navigate to="/submissions" replace />;
}

function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" richColors offset={72} />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/" element={<Landing />} />
          <Route path="/submissions" element={<Protected><SubmissionsPage /></Protected>} />
          <Route path="/assessment/:id" element={<Protected><AssessmentEditorPage /></Protected>} />
          <Route path="/print/:id" element={<Protected><PrintView /></Protected>} />
          <Route path="/panel" element={<Protected roles={["doctor", "admin"]}><DoctorPanelPage /></Protected>} />
          <Route path="/trends" element={<Protected roles={["doctor", "admin"]}><SymptomTrendsPage /></Protected>} />
          <Route path="/reference" element={<Protected roles={["doctor", "admin"]}><ClinicalReferencePage /></Protected>} />
          <Route path="/assessment/:id/clinical"
            element={<Protected roles={["doctor", "admin"]}><ClinicalSnapshotPage /></Protected>} />
          <Route path="/users" element={<Protected roles={["admin", "user_manager"]}><UsersAdminPage /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
